/**
 * ConversationHistoryPanel — read-only timeline of every agent message
 * for a page across all phases (research, framing, outline, write,
 * ground, refine). Supersedes the conversation block on the legacy
 * edit page; the active feedback loop now happens through the per-phase
 * chat in PageBuilderWorkflow, but the full transcript still lives in
 * one durable place: ManagedPageConversation.
 */
import { useEffect, useState } from 'react';
import api from '../services/api';
import type { Conversation, LLMConversationStatus } from '../types';

const STATUS_COLOR: Record<string, string> = {
  ACCEPTED: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  WAITING_FOR_USER: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  REJECTED_NEEDS_FIX: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  REJECTED_ABANDONED: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

function statusLabel(status: LLMConversationStatus | null | undefined): string {
  switch (status) {
    case 'ACCEPTED':
      return 'Accepted';
    case 'WAITING_FOR_USER':
      return 'Waiting for feedback';
    case 'REJECTED_NEEDS_FIX':
      return 'Needs fix';
    case 'REJECTED_ABANDONED':
      return 'Abandoned';
    default:
      return 'Active';
  }
}

export default function ConversationHistoryPanel({
  pageId,
}: {
  pageId: number;
}) {
  const [conv, setConv] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .getConversation(pageId)
      .then((data) => {
        if (live) setConv(data);
      })
      .catch(() => {
        if (live) setConv(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [pageId]);

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading conversation…</div>;
  }
  if (!conv || conv.messages.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        No conversation yet - run a phase from the page builder workflow to
        seed it.
      </div>
    );
  }

  const statusChipClass =
    STATUS_COLOR[conv.status as string] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';

  return (
    <div className="space-y-3 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Conversation history
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">
            ({conv.messages.length} messages)
          </span>
        </h2>
        <span
          className={`px-2 py-0.5 text-xs rounded-full ${statusChipClass}`}
        >
          {statusLabel(conv.status)}
        </span>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-300">
        Read-only transcript across every phase the agent has run for this
        page. New feedback flows through the per-phase chat on the page
        builder workflow.
      </p>

      <div className="space-y-2 border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-800">
        {conv.messages.map((msg) => {
          const isHuman = msg.entity_type === 'HUMAN';
          const isFile = msg.entity_type === 'FILE';
          const phaseLabel = msg.notes ? ` · ${msg.notes}` : '';
          return (
            <div
              key={msg.id}
              className={`flex ${isHuman ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg p-3 ${
                  isHuman
                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200'
                    : isFile
                      ? 'bg-white dark:bg-gray-800 border-2 border-purple-200 dark:border-purple-800'
                      : 'bg-green-50 dark:bg-green-900/30 text-green-900 dark:text-green-200 border border-green-100 dark:border-green-800'
                }`}
              >
                <div className="flex items-center gap-2 mb-1 text-xs font-medium">
                  <span>
                    {isHuman ? 'You' : isFile ? 'File' : 'Agent'}
                    {phaseLabel}
                  </span>
                  {!msg.processed_at && isHuman && (
                    <span className="text-[10px] bg-yellow-200 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 px-1.5 py-0.5 rounded">
                      unprocessed
                    </span>
                  )}
                </div>
                {msg.content && (
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {msg.content.length > 1500
                      ? msg.content.slice(0, 1500) + '…'
                      : msg.content}
                  </div>
                )}
                {msg.tool_calls && msg.tool_calls.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {msg.tool_calls.map((tc, i) => {
                      const name = tc.function?.name ?? 'unknown';
                      const rawArgs = tc.function?.arguments ?? '';
                      let pretty = rawArgs;
                      try {
                        const parsed = JSON.parse(rawArgs);
                        pretty = JSON.stringify(parsed, null, 2);
                      } catch {
                        /* leave raw */
                      }
                      return (
                        <div
                          key={tc.id ?? i}
                          className="rounded border border-green-200 dark:border-green-800 bg-white/60 dark:bg-gray-800 p-2 text-xs font-mono"
                        >
                          <div className="text-green-800 dark:text-green-300 font-semibold mb-1">
                            → {name}()
                          </div>
                          {rawArgs && (
                            <pre className="whitespace-pre-wrap text-green-900 dark:text-green-200 text-[11px] leading-snug max-h-60 overflow-auto">
                              {pretty}
                            </pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {!msg.content && (!msg.tool_calls || msg.tool_calls.length === 0) && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 italic">(no content)</div>
                )}
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(msg.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
